import { createReducer, on } from '@ngrx/store';
import * as CostActions from '@state/actions/cost.actions';
import {
  SelectedCostPerUnit,
  IndirectCostAssumptions,
} from '@shared/models/cost.interface';
import { LandingPageActions } from '@state/actions';

export const costFeatureKey = 'cost';

export interface State {
  selectedCostPerUnit: SelectedCostPerUnit;
  // concrete direct costs. EF = Efficiency Factor
  concreteRate: number;
  mndcRate: number;
  placingRate: number;
  labourCost: number;
  concreteSupplyPlaceEF: number;
  reinforcementRate: number;
  fixingRate: number;
  reinforcementLabourCost: number;
  reinforcementEF: number;
  formworkRate: number;
  formworkEF: number;
  structuralSteelRateConcrete: number;
  structuralSteelMassConcrete: number;
  structuralSteelEFConcrete: number;
  percentageOfAreaRequiringPT: number;
  ptEF: number;
  ballastRateConcrete: number;
  ballastConcreteEF: number;
  // steel direct costs
  structuralSteelRate: number;
  structuralSteelEF: number;
  ballastRateSteel: number;
  ballastSteelEF: number;
  // indirect costs assumptions
  factorySetupAssumption: number;
  launchUnitsAssumption: number;
  siteMobilisationAssumption: number;
  generalSiteCostsAssumption: number;
  insuranceAssumption: number;
  contractorRiskAssumption: number;
  weatherRiskAssumption: number;
  // Indirect costs for steel (from calcs spreadsheet)
  managementSupervisionAssumption: number;
  plantEquipmentLaunchAssumption: number;
}

export const defaultState: State = {
  selectedCostPerUnit: 'Prototype',
  // Init with default numbers
  // direct costs. EF = Efficiency Factor
  concreteRate: 105,
  mndcRate: 155,
  placingRate: 2,
  labourCost: 20,
  concreteSupplyPlaceEF: 75,
  reinforcementRate: 630,
  fixingRate: 15,
  reinforcementLabourCost: 20,
  reinforcementEF: 95,
  formworkRate: 75,
  formworkEF: 40,
  structuralSteelRateConcrete: 5000,
  structuralSteelMassConcrete: 10,
  structuralSteelEFConcrete: 75,
  percentageOfAreaRequiringPT: 25,
  ptEF: 70,
  ballastRateConcrete: 50,
  ballastConcreteEF: 75,
  //
  structuralSteelRate: 5000,
  structuralSteelEF: 75,
  ballastRateSteel: 0,
  ballastSteelEF: 75,
  // indirect costs assumpt
  // indirect costs
  factorySetupAssumption: 10,
  launchUnitsAssumption: 15,
  siteMobilisationAssumption: 20,
  generalSiteCostsAssumption: 30,
  insuranceAssumption: 30,
  contractorRiskAssumption: 15,
  weatherRiskAssumption: 10,
  // Indirect costs for steel (from calcs spreadsheet)
  managementSupervisionAssumption: 5,
  plantEquipmentLaunchAssumption: 5,
};

export const initialState: State = defaultState;

export const reducer = createReducer(
  initialState,
  on(CostActions.SubmitConcreteRate, (state, { concreteRate }) => {
    return {
      ...state,
      concreteRate,
    };
  }),
  on(CostActions.SubmitplacingRate, (state, { placingRate }) => {
    return {
      ...state,
      placingRate,
    };
  }),
  on(CostActions.SubmitlabourCost, (state, { labourCost }) => {
    return {
      ...state,
      labourCost,
    };
  }),
  on(CostActions.SubmitReinforcementRate, (state, { reinforcementRate }) => {
    return {
      ...state,
      reinforcementRate,
    };
  }),
  on(CostActions.SubmitfixingRate, (state, { fixingRate }) => {
    return {
      ...state,
      fixingRate,
    };
  }),
  on(CostActions.SubmitreinforcementLabourCost, (state, { reinforcementLabourCost }) => {
    return {
      ...state,
      reinforcementLabourCost,
    };
  }),
  on(CostActions.SubmitFormworkRate, (state, { formworkRate }) => {
    return {
      ...state,
      formworkRate,
    };
  }),
  on(
    CostActions.SubmitStructuralSteelRate,
    (state, { structuralSteelRate }) => {
      return {
        ...state,
        structuralSteelRate,
      };
    }
  ),
  on(
    CostActions.SubmitStructuralSteelRateConcrete,
    (state, { structuralSteelRateConcrete }) => {
      return {
        ...state,
        structuralSteelRateConcrete,
      };
    }
  ),
  on(
    CostActions.SubmitStructuralSteelMassConcrete,
    (state, { structuralSteelMassConcrete }) => {
      return {
        ...state,
        structuralSteelMassConcrete,
      };
    }
  ),
  on(
    CostActions.SubmitPercentageOfAreaRequiringPT,
    (state, { percentageOfAreaRequiringPT }) => {
      return {
        ...state,
        percentageOfAreaRequiringPT,
      };
    }
  ),
  on(CostActions.SubmitBallastRateConcrete, (state, { ballastRateConcrete }) => {
    return {
      ...state,
      ballastRateConcrete
    };
  }),
  on(CostActions.SubmitBallastRateSteel, (state, { ballastRateSteel }) => {
    return {
      ...state,
      ballastRateSteel
    };
  }),
  on(CostActions.SubmitMndcRate, (state, { mndcRate }) => {
    return {
      ...state,
      mndcRate,
    };
  }),
  on(
    CostActions.SubmitBallastConcreteEF,
    (state, { ballastConcreteEF }) => {
      return {
        ...state,
        ballastConcreteEF
      };
    }
  ),
  on(
    CostActions.SubmitConcreteSupplyPlaceEF,
    (state, { concreteSupplyPlaceEF }) => {
      return {
        ...state,
        concreteSupplyPlaceEF,
      };
    }
  ),
  on(CostActions.SubmitReinforcementEF, (state, { reinforcementEF }) => {
    return {
      ...state,
      reinforcementEF,
    };
  }),
  on(
    CostActions.SubmitStructuralSteelEFConcrete,
    (state, { structuralSteelEFConcrete }) => {
      return {
        ...state,
        structuralSteelEFConcrete,
      };
    }
  ),
  on(CostActions.SubmitPtEF, (state, { ptEF }) => {
    return {
      ...state,
      ptEF,
    };
  }),
  on(CostActions.SubmitBallastConcreteEF, (state, {ballastConcreteEF}) => {
    return {
      ...state,
      ballastConcreteEF
    };
  }),
  on(CostActions.SubmitStructuralSteelEF, (state, { structuralSteelEF }) => {
    return {
      ...state,
      structuralSteelEF,
    };
  }),
  on(CostActions.SubmitBallastSteelEF, (state, { ballastSteelEF }) => {
      return {
        ...state,
        ballastSteelEF
      };
    }
  ),
  on(CostActions.SubmitFormworkEF, (state, { formworkEF }) => {
    return {
      ...state,
      formworkEF,
    };
  }),
  on(CostActions.ResetManufacturingCosts, (state) => {
    return {
      ...state,
      mndcRate: initialState.mndcRate,
      concreteRate: initialState.concreteRate,
      placingRate: initialState.placingRate,
      labourCost: initialState.labourCost,
      reinforcementRate: initialState.reinforcementRate,
      fixingRate: initialState.fixingRate,
      reinforcementLabourCost: initialState.reinforcementLabourCost,
      formworkRate: initialState.formworkRate,
      structuralSteelRateConcrete: initialState.structuralSteelRateConcrete,
      structuralSteelMassConcrete: initialState.structuralSteelMassConcrete,
      percentageOfAreaRequiringPT: initialState.percentageOfAreaRequiringPT,
      ptEF: initialState.ptEF,
      ballastRateConcrete: initialState.ballastRateConcrete,
      ballastConcreteEF: initialState.ballastConcreteEF,
      concreteSupplyPlaceEF: initialState.concreteSupplyPlaceEF,
      formworkEF: initialState.formworkEF,
      reinforcementEF: initialState.reinforcementEF,
      structuralSteelEF: initialState.structuralSteelEF,
      structuralSteelEFConcrete: initialState.structuralSteelEF,
      structuralSteelRate: initialState.structuralSteelRate,
      ballastRateSteel: initialState.ballastRateSteel,
      ballastSteelEF: initialState.ballastSteelEF,
    };
  }),
  on(
    CostActions.UpdateSelectedCostPerUnit,
    (state, { selectedCostPerUnit }) => {
      return {
        ...state,
        selectedCostPerUnit,
      };
    }
  ),
  // assumptions - concrete
  on(
    CostActions.SubmitFactorySetupAssumption,
    (state, { factorySetupAssumption }) => {
      return {
        ...state,
        factorySetupAssumption,
      };
    }
  ),
  on(
    CostActions.SubmitLaunchUnitsAssumption,
    (state, { launchUnitsAssumption }) => {
      return {
        ...state,
        launchUnitsAssumption,
      };
    }
  ),
  on(
    CostActions.SubmitSiteMobilisationAssumption,
    (state, { siteMobilisationAssumption }) => {
      return {
        ...state,
        siteMobilisationAssumption,
      };
    }
  ),
  on(
    CostActions.SubmitGeneralSiteCostsAssumption,
    (state, { generalSiteCostsAssumption }) => {
      return {
        ...state,
        generalSiteCostsAssumption,
      };
    }
  ),
  on(
    CostActions.SubmitInsuranceAssumption,
    (state, { insuranceAssumption }) => {
      return {
        ...state,
        insuranceAssumption,
      };
    }
  ),
  on(
    CostActions.SubmitContractorRiskAssumption,
    (state, { contractorRiskAssumption }) => {
      return {
        ...state,
        contractorRiskAssumption,
      };
    }
  ),
  on(
    CostActions.SubmitWeatherRiskAssumption,
    (state, { weatherRiskAssumption }) => {
      return {
        ...state,
        weatherRiskAssumption,
      };
    }
  ),
  // assumptions - steel
  on(
    CostActions.SubmitManagementSupervisionAssumption,
    (state, { managementSupervisionAssumption }) => {
      return {
        ...state,
        managementSupervisionAssumption,
      };
    }
  ),
  on(
    CostActions.SubmitPlantEquipmentLaunchAssumption,
    (state, { plantEquipmentLaunchAssumption }) => {
      return {
        ...state,
        plantEquipmentLaunchAssumption,
      };
    }
  ),
  on(CostActions.ResetIndirectCosts, (state) => {
    return {
      ...state,
      factorySetupAssumption: initialState.factorySetupAssumption,
      launchUnitsAssumption: initialState.launchUnitsAssumption,
      siteMobilisationAssumption: initialState.siteMobilisationAssumption,
      generalSiteCostsAssumption: initialState.generalSiteCostsAssumption,
      insuranceAssumption: initialState.insuranceAssumption,
      contractorRiskAssumption: initialState.contractorRiskAssumption,
      weatherRiskAssumption: initialState.weatherRiskAssumption,
      managementSupervisionAssumption:
        initialState.managementSupervisionAssumption,
      plantEquipmentLaunchAssumption:
        initialState.plantEquipmentLaunchAssumption,
    };
  }),
  on(LandingPageActions.resetCostTables, (state) => {
    return {
      ...state,
      factorySetupAssumption: initialState.factorySetupAssumption,
      launchUnitsAssumption: initialState.launchUnitsAssumption,
      siteMobilisationAssumption: initialState.siteMobilisationAssumption,
      generalSiteCostsAssumption: initialState.generalSiteCostsAssumption,
      insuranceAssumption: initialState.insuranceAssumption,
      contractorRiskAssumption: initialState.contractorRiskAssumption,
      weatherRiskAssumption: initialState.weatherRiskAssumption,
      managementSupervisionAssumption:
        initialState.managementSupervisionAssumption,
      plantEquipmentLaunchAssumption:
        initialState.plantEquipmentLaunchAssumption,
      mndcRate: initialState.mndcRate,
      concreteRate: initialState.concreteRate,
      placingRate: initialState.placingRate,
      labourCost: initialState.labourCost,
      reinforcementRate: initialState.reinforcementRate,
      fixingRate: initialState.fixingRate,
      reinforcementLabourCost: initialState.reinforcementLabourCost,
      formworkRate: initialState.formworkRate,
      structuralSteelRateConcrete: initialState.structuralSteelRateConcrete,
      structuralSteelMassConcrete: initialState.structuralSteelMassConcrete,
      percentageOfAreaRequiringPT: initialState.percentageOfAreaRequiringPT,
      ptEF: initialState.ptEF,
      ballastRateConcrete: initialState.ballastRateConcrete,
      ballastConcreteEF: initialState.ballastConcreteEF,
      concreteSupplyPlaceEF: initialState.concreteSupplyPlaceEF,
      formworkEF: initialState.formworkEF,
      reinforcementEF: initialState.reinforcementEF,
      structuralSteelEF: initialState.structuralSteelEF,
      structuralSteelEFConcrete: initialState.structuralSteelEF,
      structuralSteelRate: initialState.structuralSteelRate,
      ballastRateSteel: initialState.ballastRateSteel,
      ballastSteelEF: initialState.ballastSteelEF,
    };
  })
);

/**
 * Because the data structure is defined within the reducer it is optimal to
 * locate our selector functions at this level. If store is to be thought of
 * as a database, and reducers the tables, selectors can be considered the
 * queries into said database. Remember to keep your selectors small and
 * focused so they can be combined and composed to fit each particular
 * use-case.
 */
export const selectSelectedCostPerUnit = (state: State) =>
  state.selectedCostPerUnit;
export const selectConcreteRate = (state: State) => state.concreteRate;
export const selectMNDCRate = (state: State) => state.mndcRate;
export const selectplacingRate = (state: State) => state.placingRate;
export const selectlabourCost = (state: State) => state.labourCost;
export const selectConcreteSupplyPlaceEF = (state: State) =>
  state.concreteSupplyPlaceEF;
export const selectReinforcementRate = (state: State) =>
  state.reinforcementRate;
export const selectfixingRate = (state: State) => state.fixingRate;
export const selectreinforcementLabourCost = (state: State) => state.reinforcementLabourCost;
export const selectReinforcementEF = (state: State) => state.reinforcementEF;
export const selectFormworkRate = (state: State) => state.formworkRate;
export const selectFormworkEF = (state: State) => state.formworkEF;
export const selectStructuralSteelRateConcrete = (state: State) =>
  state.structuralSteelRateConcrete;
export const selectStructuralSteelMassConcrete = (state: State) =>
  state.structuralSteelMassConcrete;
export const selectStructuralSteelEFConcrete = (state: State) =>
  state.structuralSteelEFConcrete;
export const selectPercentageOfAreaRequiringPT = (state: State) =>
  state.percentageOfAreaRequiringPT;
export const selectPTEF = (state: State) => state.ptEF;
export const selectBallastRateConcrete = (state: State) => state.ballastRateConcrete;
export const selectBallastConcreteEF = (state: State) => state.ballastConcreteEF;
export const selectBallastRateSteel = (state: State) => state.ballastRateSteel;
export const selectBallastSteelEF = (state: State) => state.ballastSteelEF;
export const selectStructuralSteelRate = (state: State) =>
  state.structuralSteelRate;
export const selectStructuralSteelEF = (state: State) =>
  state.structuralSteelEF;
export const selectFactorySetup = (state: State) =>
  state.factorySetupAssumption;
export const selectLaunchUnits = (state: State) => state.launchUnitsAssumption;
export const selectSiteMobilisation = (state: State) =>
  state.siteMobilisationAssumption;
export const selectGeneralSiteCosts = (state: State) =>
  state.generalSiteCostsAssumption;
export const selectInsurance = (state: State) => state.insuranceAssumption;
export const selectContractorRisk = (state: State) =>
  state.contractorRiskAssumption;
export const selectWeatherRisk = (state: State) => state.weatherRiskAssumption;
export const selectManagementSupervision = (state: State) =>
  state.managementSupervisionAssumption;
export const selectPlantEquipmentLaunch = (state: State) =>
  state.plantEquipmentLaunchAssumption;

export const selectIndirectCostAssumptions = (
  state: State
): IndirectCostAssumptions => {
  return {
    factorySetup: state.factorySetupAssumption,
    launchUnits: state.launchUnitsAssumption,
    siteMobilisation: state.siteMobilisationAssumption,
    generalSiteCosts: state.generalSiteCostsAssumption,
    insurance: state.insuranceAssumption,
    contractorRisk: state.contractorRiskAssumption,
    weatherRisk: state.weatherRiskAssumption,
    managementSupervisionAssumption: state.managementSupervisionAssumption,
    plantEquipmentLaunchAssumption: state.plantEquipmentLaunchAssumption,
  };
};
